import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, RotateCcw } from "lucide-react";

/**
 * Top-level React error boundary — prevents one component crashing the whole
 * SPA. Renders a branded, actionable fallback UI and logs the error for
 * observability. `resetKey` prop (usually the current pathname) triggers a
 * boundary reset on route change so users don't get stuck on the error page.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface in the browser console — hook up Sentry here if desired.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] Uncaught error:", error, info);
  }

  componentDidUpdate(prevProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        className="min-h-screen bg-[var(--gt-bg-marketing)] text-[var(--gt-ink)] flex flex-col"
        data-testid="app-error-boundary"
      >
        <div className="flex-1 flex items-center justify-center px-6 py-16">
          <div className="max-w-lg text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-sm bg-[var(--gt-accent)]/10 text-[var(--gt-accent)] mb-6">
              <AlertTriangle size={26} strokeWidth={1.75} />
            </div>
            <div className="gt-label mb-3">Something went wrong</div>
            <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-4">
              This page hit a snag.
            </h1>
            <p className="text-[var(--gt-ink-2)] leading-relaxed text-sm mb-8">
              We've logged the error and our team will take a look. In the
              meantime, try refreshing or head back home.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                onClick={this.handleRetry}
                className="gt-btn-primary"
                data-testid="error-boundary-retry"
              >
                <RotateCcw size={14} /> Try again
              </button>
              <Link to="/" className="gt-btn-secondary" data-testid="error-boundary-home">
                Back to home
              </Link>
            </div>
            {process.env.NODE_ENV !== "production" && this.state.error && (
              <pre
                className="text-left mt-8 text-[11px] bg-white border border-[var(--gt-line)] rounded-sm p-4 overflow-auto max-h-56 text-[var(--gt-ink-3)]"
                data-testid="error-boundary-details"
              >
                {String(this.state.error?.stack || this.state.error?.message || this.state.error)}
              </pre>
            )}
          </div>
        </div>
      </div>
    );
  }
}
